import { useCallback, useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";
import { Receipt, CaretDown, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { InvoiceDialog } from "./InvoiceDialog";
import { hm, money, monthLabel, isoDay, StatusPill } from "./hrUtils";

const FIELDS = [["trading_name", "Trading name"], ["email", "Email"], ["phone", "Phone"], ["utr", "UTR"], ["vat_number", "VAT number"], ["account_name", "Account name"], ["bank_name", "Bank"], ["sort_code", "Sort code"], ["account_number", "Account number"], ["payment_terms", "Payment terms"]];

/** Monthly invoices built from clocked time; invoice details (self only) and the list with statuses. */
export const InvoicesTab = ({ userId, isOps, isSelf, profile, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [month, setMonth] = useState(isoDay(new Date()).slice(0, 7));
  const [openInv, setOpenInv] = useState(null);
  const [details, setDetails] = useState(profile?.invoice_details || {});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await API.get(`/hr/invoices${userId ? `?user_id=${userId}` : ""}`); setRows(data.invoices); } catch (e) { toast.error(formatApiError(e)); }
  }, [userId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setDetails(profile?.invoice_details || {}); }, [profile]);

  const build = async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/hr/invoices/build", { month, user_id: userId || undefined });
      toast.success(`${monthLabel(month)} invoice drafted from ${hm(data.total_minutes)} clocked`);
      load(); setOpenInv(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const open = async (r) => { try { const { data } = await API.get(`/hr/invoices/${r.id}`); setOpenInv(data); } catch (e) { toast.error(formatApiError(e)); } };
  const del = async (r) => {
    if (!window.confirm(`Delete the ${monthLabel(r.month)} draft?`)) return;
    try { await API.delete(`/hr/invoices/${r.id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveDetails = async () => {
    try { await API.put("/hr/me/invoice-details", details); toast.success("Invoice details saved"); onChanged?.(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const rate = profile?.rate ? `${money(profile.rate)} per ${profile.rate_type === "hourly" ? "hour" : "day"}` : "no rate set";
  return (
    <div className="space-y-5" data-testid="hr-invoices-tab">
      <div className="rounded-xl border border-border bg-card p-4 flex flex-wrap items-end gap-3">
        <div>
          <Label>Month</Label>
          <Input type="month" value={month} max={isoDay(new Date()).slice(0, 7)} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="hr-invoice-month" />
        </div>
        <Button onClick={build} disabled={busy || !profile?.rate} data-testid="hr-invoice-build"><Receipt className="h-4 w-4 mr-2" /> Build invoice from clocked time</Button>
        <p className="text-xs text-muted-foreground w-full sm:w-auto">Rate: <strong>{rate}</strong>{!profile?.rate && " — an Operations Director sets this in the HR Hub"}</p>
      </div>
      <div className="rounded-xl border border-border divide-y divide-border">
        <p className="px-3 py-2 text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">Invoices</p>
        {rows.length === 0 && <p className="p-4 text-sm text-muted-foreground">No invoices yet — build this month's from your clocked time.</p>}
        {rows.map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-muted/40 cursor-pointer" onClick={() => open(r)} data-testid={`hr-invoice-row-${r.id}`}>
            <div className="min-w-0">
              <p className="text-sm font-medium">{r.invoice_number || "Draft"} · {monthLabel(r.month)}</p>
              <p className="text-xs text-muted-foreground">{hm(r.total_minutes)} · {r.days_worked} day(s){r.paid_at ? ` · paid ${new Date(r.paid_at).toLocaleDateString("en-GB")}` : ""}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="font-semibold tabular-nums">{money(r.total)}</span>
              <StatusPill status={r.status} />
              {r.status === "draft" && <button className="text-muted-foreground hover:text-destructive" onClick={(e) => { e.stopPropagation(); del(r); }} data-testid={`hr-invoice-delete-${r.id}`}><Trash size={16} /></button>}
            </div>
          </div>
        ))}
      </div>
      {isSelf && (
        <Collapsible className="rounded-xl border border-border">
          <CollapsibleTrigger className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold" data-testid="hr-invoice-details-toggle">My invoice details <CaretDown className="h-4 w-4" /></CollapsibleTrigger>
          <CollapsibleContent className="px-4 pb-4 space-y-3">
            <p className="text-xs text-muted-foreground">Shown on your invoice PDF. Only you and the Operations Directors can see these.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {FIELDS.map(([k, l]) => (
                <div key={k}><Label>{l}</Label><Input value={details[k] || ""} onChange={(e) => setDetails({ ...details, [k]: e.target.value })} data-testid={`hr-detail-${k}`} /></div>
              ))}
              <div className="sm:col-span-2"><Label>Address</Label><Input value={details.address || ""} onChange={(e) => setDetails({ ...details, address: e.target.value })} data-testid="hr-detail-address" /></div>
            </div>
            <Button size="sm" onClick={saveDetails} data-testid="hr-invoice-details-save">Save details</Button>
          </CollapsibleContent>
        </Collapsible>
      )}
      {openInv && <InvoiceDialog invoice={openInv} isOps={isOps} isOwner={isSelf} onClose={() => setOpenInv(null)} onChanged={load} />}
    </div>
  );
};
